import { Component, Input, OnInit, ChangeDetectionStrategy } from "@angular/core";
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { ModalController, Platform } from "@ionic/angular";
import { Device } from "src/components/shared-components/class/general-classes/device";

import { ApplicationTheme } from "src/components/shared-components/enum/enumMisc";
import { iConnectBoxConfigurationData } from "src/model/interface/iConnectBoxConfigurationData";
import { iConnectBoxConfigurationResponse } from "src/model/interface/iConnectBoxConfigurationResponse";
import { DeviceService } from "src/service/device.service";
import { ThemeService } from "src/service/theme.service";

@Component({
    selector: "app-popup-connect-box-management",
    templateUrl: "./popup-connect-box-management.component.html",
    styleUrls: ["./popup-connect-box-management.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PopupConnectBoxManagementComponent implements OnInit {
  public isLoadEnterConnectBoxData: boolean = true;
  public isLoadCopyConfigurationParameters: boolean = false;
  public isMobileDevice: boolean = false;
  public toolbarCssClass: string = "";
  public showErrorMessageOnClick: boolean = false;
  public formConnectBoxManagement: UntypedFormGroup = null;
  public connectBoxConfiguration: iConnectBoxConfigurationResponse =
    {} as iConnectBoxConfigurationResponse;

  @Input() currentIdArea: number;
  @Input() idEnvironment: number;
  @Input() isConfigurationData: boolean;
  @Input() configurationData: iConnectBoxConfigurationResponse;

  constructor(
    private _platform: Platform,
    private _themeService: ThemeService,
    private _modalController: ModalController,
    private _fb: UntypedFormBuilder,
    private _deviceService: DeviceService
  ) { }

  async ngOnInit(): Promise<void> {
    this.checkMobileCondition();
    this.handleViewConfigurationData();
    this.initializeFormConnectBocManagement();
  }

  /** Label of the parameter just copied (shows "Copied!" on its button for a moment) */
  public copiedParameter: string = null;
  /** Whether the password is shown in clear */
  public isPasswordVisible: boolean = false;
  private _copiedTimeout: ReturnType<typeof setTimeout> = null;

  /** Connection parameters to copy into the ConnectBox */
  public get connectionParameters(): Array<{ label: string; value: any }> {
    const cfg = this.connectBoxConfiguration;
    return [
      { label: "Server Host", value: cfg?.serverHost },
      { label: "Server Port", value: cfg?.serverPort },
      { label: "ClientID", value: cfg?.clientID },
      { label: "MQTT Version", value: cfg?.mqttVersion },
      { label: "QoS", value: cfg?.qos },
      { label: "Uplink Topic", value: cfg?.uplink },
      { label: "Downlink Topic", value: cfg?.downlink },
    ];
  }

  /**
   * Copy a parameter to the clipboard
   * @param label parameter name (to show the feedback on its button)
   * @param value parameter value
   */
  public async onClickCopyParameter(label: string, value: any): Promise<void> {
    try {
      await navigator.clipboard.writeText(value == null ? "" : String(value));
      this.copiedParameter = label;
      clearTimeout(this._copiedTimeout);
      this._copiedTimeout = setTimeout(() => (this.copiedParameter = null), 1800);
    } catch {
      // Clipboard not available (e.g. insecure context): the value stays selectable by hand
    }
  }

  /**
   * Handle the specific gateway type form submission: Will be changed once the backend is ready
   */
  public async onSubmitConnectBoxConfigurationData(): Promise<void> {
    if (this.isLoadEnterConnectBoxData) {
      if (
        this.formConnectBoxManagement.invalid &&
        !this.formConnectBoxManagement.touched
      ) {
        this.showErrorMessageOnClick = true;
      } else if (
        this.formConnectBoxManagement.touched &&
        this.formConnectBoxManagement.valid
      ) {
        this.showErrorMessageOnClick = false;
        const connectBoxConfigData: iConnectBoxConfigurationData = {
          idEnvironment: this.idEnvironment,
          description: this.formConnectBoxManagement.value.description,
          serialNumber: this.formConnectBoxManagement.value.serialNumber,
        };

        const connectBoxData: Device =
          await this._deviceService.addConnectBoxConfigurationData(
            connectBoxConfigData
          );

        if (Object.keys(connectBoxData).length !== 0) {
          this.connectBoxConfiguration = await this.loadConnectBoxConfiguration(
            connectBoxData.id
          );
        }
      }
    }

    /**
     * The next step which provides configuration parameters to copy is available only if the connectBoxConfiguration is not empty
     */
    if (Object.keys(this.connectBoxConfiguration).length !== 0) {
      this.isLoadEnterConnectBoxData = false;
      this.isLoadCopyConfigurationParameters = true;
    }
  }

  /**
   * Close the modal and retain the main page
   */
  public onClosePopupConnectBoxManagement() {
    if (this.isLoadEnterConnectBoxData) {
      this._modalController.dismiss();
    } else {
      this._modalController.dismiss(this.connectBoxConfiguration);
    }
  }

  /**
   * Get the navigation params as string
   * @returns string
   */
  public getNavigationParamsBack(): string {
    const idAreaString: string = this.currentIdArea?.toString();
    const idEnvironmentString: string = this.idEnvironment?.toString();
    return `${idAreaString}/${idEnvironmentString}`;
  }

  /**
   * Checks the conditions required to show an error message
   * @param formControlToCheck the formControlName
   * @returns
   */
  public checkConditionsToShowErrorMessage(
    formControlToCheck: string = ""
  ): boolean {
    if (
      formControlToCheck === undefined ||
      formControlToCheck === "" ||
      formControlToCheck === null
    ) {
      return false;
    }

    const targetFormControl: AbstractControl =
      this.formConnectBoxManagement.get(formControlToCheck);

    return !!(
      targetFormControl &&
      targetFormControl.touched &&
      targetFormControl.invalid
    );
  }

  /**
   * Check mobile condition.
   * It load app version and it sets size of floating container
   */
  private checkMobileCondition(): void {
    this.isMobileDevice =
      this._platform.is("ios") || this._platform.is("android");

    if (this.isMobileDevice) {
      this.toolbarCssClass = "force-show-top-toolbar";
    } else {
      this.toolbarCssClass = "force-hide-top-toolbar";
    }
  }

  /**
   * Initializes the connect box management form for the description and serial number
   */
  private initializeFormConnectBocManagement(): void {
    this.formConnectBoxManagement = this._fb.group({
      description: ["", Validators.required],
      serialNumber: ["", Validators.required],
    });
  }

  /**
   * This handles the content of the popup in the case the view connect box configuration data is clicked on the device page
   */
  private async handleViewConfigurationData() {
    if (this.isConfigurationData) {
      this.isLoadEnterConnectBoxData = false;
      this.isLoadCopyConfigurationParameters = true;
    }
    if (!this.isConfigurationData) return;
    if (
      this.configurationData &&
      Object.keys(this.configurationData).length !== 0
    ) {
      this.connectBoxConfiguration = this.configurationData;
    }
  }

  /**
   * Load the configuration for connect box
   * @param idDevice device id
   * @returns
   */
  private async loadConnectBoxConfiguration(idDevice: number) {
    return await this._deviceService.getConfigurationForConnectBox(idDevice);
  }
}
